import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { useAccount } from '@/contexts/AccountContext';
import { useTransactions } from '@/contexts/TransactionContext';
import { Loader2, Search, Filter, Plus } from "lucide-react";
import { useSearchParams } from 'react-router-dom';

interface Wallet {
  id: string;
  name: string;
  balance: number;
  is_active: boolean;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const TransactionHistory = () => {
  const { toast } = useToast();
  const { accountType } = useAccount();
  const { transactions, loading, error, refreshTransactions } = useTransactions();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'transaction_date' | 'amount'>('transaction_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<string>('');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadWallets();
  }, [accountType]);

  useEffect(() => {
    const walletId = searchParams.get('wallet');
    if (walletId) {
      setSelectedWallet(walletId);
    }
  }, [searchParams]);

  const loadWallets = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Error",
          description: "Please sign in to view your transactions",
          variant: "destructive",
        });
        return;
      }

      // Get all wallets for the user and account type
      const { data: allWallets, error: walletsError } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', accountType);

      if (walletsError) {
        console.error('Wallets error:', walletsError);
        toast({
          title: "Error",
          description: "Failed to load wallets. Please try again.",
          variant: "destructive",
        });
        return;
      }

      if (!allWallets || allWallets.length === 0) {
        toast({
          title: "No Wallets",
          description: "Please create a wallet first.",
          variant: "destructive",
        });
        return;
      }

      // Set all wallets for the dropdown
      setWallets(allWallets);
      
      // If there's no wallet ID in the URL, set the active wallet
      const walletId = searchParams.get('wallet');
      if (!walletId) {
        // Get the active wallet
        const { data: activeWallet, error: activeError } = await supabase
          .from('wallets')
          .select('*')
          .eq('user_id', user.id)
          .eq('type', accountType)
          .eq('is_active', true)
          .single();

        if (activeError && activeError.code !== 'PGRST116') {
          console.error('Active wallet error:', activeError);
          toast({
            title: "Error",
            description: "Failed to load active wallet. Please try again.",
            variant: "destructive",
          });
          return;
        }

        // Set the active wallet as selected
        if (activeWallet) {
          setSelectedWallet(activeWallet.id);
        } else if (allWallets.length > 0) {
          // If no active wallet, set the first one as active
          const firstWallet = allWallets[0];
          const { error: updateError } = await supabase
            .from('wallets')
            .update({ is_active: true })
            .eq('id', firstWallet.id);
          
          if (updateError) {
            console.error('Update wallet error:', updateError);
            toast({
              title: "Error",
              description: "Failed to update wallet status. Please try again.",
              variant: "destructive",
            });
            return;
          }
          
          setSelectedWallet(firstWallet.id);
        }
      }
    } catch (error) {
      console.error('Error loading wallets:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const filteredTransactions = transactions
    .filter(transaction => {
      const matchesWallet = !selectedWallet || transaction.wallet_id === selectedWallet;
      const matchesSearch = 
        transaction.place?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transaction.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        transaction.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = categoryFilter === 'all' || transaction.category === categoryFilter;
      
      return matchesWallet && matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'transaction_date') {
        return sortOrder === 'asc' 
          ? new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime()
          : new Date(b.transaction_date).getTime() - new Date(a.transaction_date).getTime();
      } else {
        return sortOrder === 'asc'
          ? a.amount - b.amount
          : b.amount - a.amount;
      }
    });

  const categories = Array.from(new Set(transactions.map(t => t.category)));

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-red-500">{error}</p>
        <Button onClick={refreshTransactions}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Transaction History</h2>
        <div className="flex items-center space-x-2">
          {wallets.map((wallet) => (
            <Button
              key={wallet.id}
              className={selectedWallet === wallet.id ? "" : "bg-secondary"}
              onClick={() => setSelectedWallet(wallet.id)}
            >
              {wallet.name} (Rp {wallet.balance.toLocaleString('id-ID')})
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(value: 'transaction_date' | 'amount') => setSortBy(value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="transaction_date">Date</SelectItem>
              <SelectItem value="amount">Amount</SelectItem>
            </SelectContent>
          </Select>
          <Button className="bg-secondary" onClick={toggleSortOrder}>
            <Filter className="h-4 w-4 mr-2" />
            {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          </Button>
        </div>

        {transactions.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="space-y-4">
              <p className="text-lg text-muted-foreground">No transactions yet</p>
              <Button className="bg-primary">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Transaction
              </Button>
            </div>
          </Card>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Wallet</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>
                      {formatDate(transaction.transaction_date)}
                    </TableCell>
                    <TableCell>{transaction.category}</TableCell>
                    <TableCell>{wallets.find(w => w.id === transaction.wallet_id)?.name || '-'}</TableCell>
                    <TableCell className="text-right">
                      Rp {transaction.amount.toLocaleString('id-ID')}
                    </TableCell>
                    <TableCell>{transaction.description || '-'}</TableCell>
                  </TableRow>
                ))}
                {filteredTransactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center">
                      No transactions found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory; 